import { Component, OnInit } from '@angular/core';
import { CatalogoSolicitudesService } from 'src/app/services/integracion-pos/catalogo-solicitudes.service';
import {
  CarrilTipo, ComercioFlujo, PasoCatalogo, TipoSolicitud,
  conclusion, construirCarriles, formatoFecha, nombrePaso, pasosQueNadieHace, seAparta,
  textoDecideCorto,
} from './tipos-flujos.dominio';

/**
 * «Tipos y flujos» — consola de CrediTotal, sólo lectura.
 *
 * Contesta UNA pregunta: **¿en qué se diferencian las formas de entrar una venta al canal POS?**
 * Y la contesta dos veces: escrita arriba, y mostrada abajo tipo por tipo.
 *
 * 🔴 EL WORKFLOW SE CASA AL TIPO DE SOLICITUD, NO AL COMERCIO — corrección de Juan, 2026-09-20:
 * *«el workflow se casa a la compra web, se casa a la compra de comercio. No se casa a cada uno de
 * nuestros comercios»*. Por eso aquí no hay nada por comercio: ni carriles, ni columnas, ni el
 * filtro de la cabecera. Lo único que queda es un control —si la versión propia de algún comercio
 * llegara a cambiar un recorrido, se dice en una línea— y hoy no cambia ninguno.
 *
 * 🔴 Y NO HAY DIBUJO, que es una decisión y no un olvido. Tres de los cuatro tipos tienen UN paso
 * y el cuarto tiene dos: un lienzo para dos puntos es un adorno caro, y la regla del análisis de
 * diseño es explícita —*un dibujo de más es peor que ninguno*—. Lo que se dibujaba (la
 * comparación) hoy lo dice la frase de arriba, que además se calcula con los datos: el día que un
 * tipo cambie de recorrido, la frase cambia sola.
 *
 * Lo que se fue en las tres vueltas de este mismo día, todas con Juan mirando la pantalla: la
 * matriz de 4 × 7 con los rótulos cortados, las tarjetas de métricas, las cuatro tarjetas en
 * rejilla con un dibujo cada una, los carriles por comercio, y el paso `HABILITAR_CUPO`, que se
 * retiró del canal el 2026-09-19.
 */
@Component({
  selector: 'app-tipos-flujos',
  templateUrl: './tipos-flujos.component.html',
  styleUrls: ['./tipos-flujos.component.css'],
})
export class TiposFlujosComponent implements OnInit {

  cargando = true;
  error = '';

  pasos: PasoCatalogo[] = [];
  tipos: TipoSolicitud[] = [];
  comercios: ComercioFlujo[] = [];

  /** Un renglón por tipo, con su recorrido ya resuelto. */
  carriles: CarrilTipo[] = [];
  /** Los pasos que existen en el catálogo y que hoy no hace ningún tipo. Se dicen, no se muestran. */
  apagados: PasoCatalogo[] = [];
  frase = '';

  vigenciaAbierta = false;

  textoDecideCorto = textoDecideCorto;

  constructor(private _catalogo: CatalogoSolicitudesService) { }

  ngOnInit(): void { this.cargar(); }

  cargar = async (): Promise<void> => {
    this.cargando = true;
    this.error = '';
    try {
      const c: any = await this._catalogo.getCatalogo();
      this.pasos = Array.isArray(c?.pasos) ? c.pasos : [];
      this.tipos = Array.isArray(c?.tipos) ? c.tipos : [];
      this.comercios = Array.isArray(c?.comercios_con_flujo_propio) ? c.comercios_con_flujo_propio : [];
    } catch (e: any) {
      this.pasos = [];
      this.tipos = [];
      this.comercios = [];
      this.error = e?.error?.msg || 'No se pudo traer el catálogo de tipos y flujos. Intenta de nuevo en un momento.';
    } finally {
      this.carriles = construirCarriles(this.tipos, this.pasos, null);
      this.apagados = pasosQueNadieHace(this.carriles, this.pasos);
      this.frase = conclusion(this.carriles);
      this.cargando = false;
    }
  };

  get hoy(): string { return formatoFecha(new Date()); }

  /**
   * 🔴 EL CONTROL. El workflow es del tipo, pero la versión propia de un comercio sigue existiendo
   * en la base: si alguna llegara a cambiar un recorrido, esta pantalla estaría contando otra cosa
   * que la que corre. Hoy no cambia ninguna —y por eso esta línea no se ve—; el día que cambie, se
   * ve sola. Es más barato que enterarse por un cliente.
   */
  get comerciosQueCambian(): ComercioFlujo[] {
    return this.comercios.filter((m) => this.tipos.some((t) => seAparta(t, this.pasos, m)));
  }

  /** «Elegir el plan» existe en el catálogo y hoy no lo hace ningún tipo. Se dice en una línea. */
  get lineaApagados(): string {
    if (!this.apagados.length) return '';
    const nombres = this.apagados.map((p) => `«${nombrePaso(p.code)}»`);
    const uno = this.apagados.length === 1;
    return `${nombres.join(' y ')} ${uno ? 'existe' : 'existen'} en el catálogo, pero hoy `
      + `${uno ? 'ningún tipo lo pide' : 'ningún tipo los pide'}: el cliente no `
      + `${uno ? 'lo ve' : 'los ve'} en ningún recorrido.`;
  }
}
